import { useEffect, useRef } from 'react'

import useApiGet from 'Pods/Shared/Hooks/useApiGet'
import { resolveViewerId } from 'Pods/Shared/Hooks/useViewerId'

import { publishGenerationStatus, setGenerationResult } from './actions'
import { buildLastResult } from './buildLastResult'
import { generationStatusStore } from './index'

const STUDIO_MOUNT_ID = 'three-d-studio-elements'

// Grace window before an in-flight flag with an empty `/runs/pending` is treated as
// stale. /ai writes `isGenerating: true` at generation START, before the POST persists
// the run, so for a brief window a genuinely-fresh run is not yet returned by pending.
// A flag younger than this is assumed mid-startup (keep it); older is stale (clear it).
const STALE_FLAG_GRACE_MS = 30000

// On /ai the studio drives the store directly (and mirrors into it), so this watcher
// stays out of the way to avoid a duplicate subscription.
function isStudioPresent() {
  return typeof document !== 'undefined' && !!document.getElementById(STUDIO_MOUNT_ID)
}

function toTimestamp(value) {
  if (!value) {
    return null
  }
  const ms = new Date(value).getTime()
  return Number.isNaN(ms) ? null : ms
}

function ensureCableConsumer() {
  if (typeof window === 'undefined') {
    return null
  }
  window.App = window.App || {}
  if (!window.App.cable?.subscriptions) {
    // eslint-disable-next-line global-require
    const ActionCable = require('actioncable')
    window.App.cable = ActionCable.createConsumer()
  }
  return window.App.cable
}

// Off-/ai watcher for the global generation-status store. /ai is the sole producer;
// this hook does NOT start generations. It runs from the top nav (mounted on every
// page) so a run started in /ai still resolves to succeeded/failed when the user has
// navigated away or is on another tab - the /ai subscription dies with that page, so
// without this nothing off /ai would catch the completion broadcast.
//
// To keep page loads cheap it only touches the backend when THIS browser already
// believes a run is in flight (persisted to localStorage by /ai); an idle store does
// nothing. When the backend confirms a run is still pending it (re)subscribes to catch
// the completion broadcast. When the backend reports nothing pending it uses the flag's
// age to decide: an OLD flag is stale (a run whose completion no mounted tab observed,
// so it never cleared) and is cleared rather than resurrected - resurrecting would flash
// a "previous generation" result on an unrelated page load; a JUST-STARTED flag is a run
// /ai only moments ago wrote `isGenerating: true` for, not yet registered as pending, so
// it is left in flight (the originating tab drives it) - clearing it would clobber a live
// generation across every tab.
//
// It reconciles both at mount and whenever the store transitions idle -> generating
// afterwards: another tab starting a run writes localStorage, which rehydrates this
// tab's store via the cross-tab `storage` listener (see index.js). Without reacting to
// that, an already-open tab would never subscribe and could stay stuck in-flight if the
// /ai tab closed before the terminal broadcast.
export default function useGenerationStatusWatcher({ isCurrentUser }) {
  const { getData } = useApiGet()
  // One GeneratorRunsChannel subscription per in-flight run (paid tiers run several
  // concurrently), keyed by String(runId). Mirrors useGeneratorRunChannel on /ai.
  const subscriptionsRef = useRef(new Map())
  // useApiGet's getData is recreated whenever Sentry finishes loading (its useSentry
  // dep flips), so keep it in a ref to keep it out of the effect deps - otherwise the
  // effect tears down and re-subscribes the live run channel mid-generation, risking a
  // missed completion broadcast. Mirrors navigate/location refs in useGeneratorRunChannel.
  const getDataRef = useRef(getData)
  getDataRef.current = getData

  useEffect(() => {
    if (!isCurrentUser || isStudioPresent()) {
      return undefined
    }

    // Guards re-entrancy: reconcile() re-publishes status (an idle -> generating store
    // write), which re-fires our own store listener. Only the first transition into a
    // generating state should hit the backend; it resets when the run settles.
    let reconciling = false
    // One-shot re-check queued when pending is empty inside the grace window (see below).
    let recheckTimer = null

    function cleanup() {
      subscriptionsRef.current.forEach((subscription) => subscription.unsubscribe())
      subscriptionsRef.current.clear()
    }

    function unsubscribeRun(runIdKey) {
      const subscription = subscriptionsRef.current.get(runIdKey)
      if (subscription) {
        subscription.unsubscribe()
        subscriptionsRef.current.delete(runIdKey)
      }
    }

    // Settles one run: drop its subscription and publish the remaining in-flight
    // count; only the last run to settle flips the store back to idle.
    function settleRun(runIdKey, attributes) {
      unsubscribeRun(runIdKey)
      if (attributes) {
        setGenerationResult(buildLastResult(attributes))
      }
      const remaining = subscriptionsRef.current.size
      publishGenerationStatus({
        isGenerating: remaining > 0,
        activeRunCount: remaining,
        ...(remaining === 0 ? { runId: undefined } : {}),
      })
    }

    function subscribe(runId) {
      const cable = ensureCableConsumer()
      if (!cable?.subscriptions || !runId) {
        return
      }
      const runIdKey = String(runId)
      if (subscriptionsRef.current.has(runIdKey)) {
        return
      }
      subscriptionsRef.current.set(runIdKey, cable.subscriptions.create(
        { channel: 'GeneratorRunsChannel', run_id: runId },
        {
          rejected() {
            settleRun(runIdKey, null)
          },
          received(message) {
            if (message?.type !== 'generator/runs/update') {
              return
            }
            settleRun(runIdKey, message?.data?.attributes || {})
          },
        }
      ))
    }

    // Reconcile the hydrated (possibly stale) in-flight state against the backend.
    function reconcile() {
      if (recheckTimer) {
        clearTimeout(recheckTimer)
        recheckTimer = null
      }

      const { generationStartedAt } = generationStatusStore.getState()
      getDataRef.current({
        path: '/api/internal/generator/runs/pending',
        onSuccess: (response) => {
          const sessions = response.data?.data || []
          const pendingRuns = sessions.flatMap(({ session, runs }) => (
            (runs || [])
              .filter((run) => run?.id != null)
              .map((run) => ({ ...run, session }))
          ))

          if (pendingRuns.length > 0) {
            const [first] = pendingRuns
            publishGenerationStatus({
              isGenerating: true,
              runId: first.id,
              sessionId: first.session,
              generationStartedAt: toTimestamp(first.created_at),
              activeRunCount: pendingRuns.length,
            })
            pendingRuns.forEach((run) => subscribe(run.id))
            return
          }

          // Nothing pending. This is ambiguous: either the run finished a while ago and
          // no mounted tab observed it (stale flag - clear it, but do NOT resurrect it
          // from /runs/:id, which would flash a "previous generation" result), or a run
          // /ai just started is not yet registered as pending (startup race - leave it,
          // the originating tab drives it to completion). `/runs/pending` cannot tell
          // these apart, so fall back to the flag's age: only a flag older than the grace
          // window is stale. Clearing a just-started run would clobber a live generation
          // across every tab.
          const age = generationStartedAt ? Date.now() - generationStartedAt : Infinity
          if (age < STALE_FLAG_GRACE_MS) {
            // The store stays isGenerating, so the store listener will not re-fire on its
            // own; without a scheduled re-check a run that never registers as pending (e.g.
            // the /ai tab closed before its POST persisted) would leave the nav stuck in
            // flight forever. Reset the guard and re-check once the grace window elapses.
            reconciling = false
            recheckTimer = setTimeout(maybeReconcile, STALE_FLAG_GRACE_MS - age)
            return
          }

          publishGenerationStatus({ isGenerating: false, runId: undefined, activeRunCount: 0 })
        },
        onError: () => publishGenerationStatus({ isGenerating: false, runId: undefined, activeRunCount: 0 }),
      })
    }

    function maybeReconcile() {
      const { isGenerating, userId } = generationStatusStore.getState()

      // localStorage is shared by every user of this browser. If the persisted run is
      // owned by a different account (user A starts a run, then user B signs in here),
      // do NOT reconcile it against B's /runs/pending - that would clear A's in-flight
      // status before A returns. Only skip on a positive ownership mismatch; an unknown
      // owner or an unresolved viewer falls through so legacy state and startup races
      // still self-heal. The menu/toast already hide mismatched owners from B.
      const viewerId = resolveViewerId()
      if (userId != null && viewerId != null && userId !== viewerId) {
        reconciling = false
        return
      }

      if (isGenerating && !reconciling) {
        reconciling = true
        reconcile()
      } else if (!isGenerating) {
        reconciling = false
      }
    }

    maybeReconcile()
    const unsubscribeStore = generationStatusStore.subscribe(maybeReconcile)

    return () => {
      unsubscribeStore()
      cleanup()
      if (recheckTimer) {
        clearTimeout(recheckTimer)
      }
    }
  }, [isCurrentUser])
}
